import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'

import { SERIES_COMMENT_PAGE_SIZE } from '@/common/services/seriesPage/constants'
import type { SeriesDiscussionData } from '@/common/services/seriesPage/types'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { getBodyForHiddenEntity } from '@/client/services/hidden'
import mutateAndRefreshPage from '@/client/services/trpcMutations'
import { useTRPC, useTRPCClient } from '@/client/trpc'

import Discussion from '@/components/Discussion/Discussion'
import type { DiscussionCommentProps } from '@/components/Discussion/types'

export default function SeriesDiscussion({
  series,
}: {
  series: SeriesDiscussionData
}): React.ReactElement {
  const currentUser = useUser()

  const tutorIds = series.hosts.map(host => host.userId)

  const seriesId = series.id
  const trpc = useTRPC()
  const trpcClient = useTRPCClient()
  const queryClient = useQueryClient()

  // The first page arrives through hydration (the page's hydrate step seeds what the
  // loader fetched); further pages arrive through the comments procedure. Both go
  // through pageFromComments server-side, so the cursor has exactly one owner.
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    trpc.series.comments.infiniteQueryOptions(
      { seriesId, limit: SERIES_COMMENT_PAGE_SIZE },
      { getNextPageParam: page => page.nextCursor, initialCursor: null }
    )
  )
  const visibleComments = (data?.pages ?? []).flatMap(page => page.comments)

  const [handlePost] = useAPIWrapper(
    async (message: string) => {
      await mutateAndRefreshPage(trpcClient.series.postComment.mutate({ seriesId, message }))
      await queryClient.invalidateQueries(trpc.series.comments.pathFilter())
    },
    [seriesId],
    'Post Series Comment'
  )

  const [handleDelete] = useAPIWrapper(
    async (commentId: number) => {
      await mutateAndRefreshPage(trpcClient.series.deleteComment.mutate({ commentId }))
      await queryClient.invalidateQueries(trpc.series.comments.pathFilter())
    },
    [],
    'Delete Series Comment'
  )

  const [handleLoadMore] = useAPIWrapper(
    async () => {
      await fetchNextPage()
    },
    [fetchNextPage],
    'Load Series Comments'
  )

  const hasMore = hasNextPage && !isFetchingNextPage

  const relevantDiscussionComments: DiscussionCommentProps[] = visibleComments.map(
    seriesComment => ({
      id: seriesComment.id,
      message:
        seriesComment.hiddenAt != null
          ? getBodyForHiddenEntity('series-comment', 'plain-text')
          : (seriesComment.message ?? ''),
      createdAt: seriesComment.createdAt,
      isHidden: seriesComment.hiddenAt != null,
      user: {
        id: seriesComment.author.id,
        avatarUrl: seriesComment.author.avatarUrl ?? '',
        username: seriesComment.author.username,
        progressionLevel: seriesComment.author.progressionLevel,
        displayName: seriesComment.author.displayName,
        isTutor: tutorIds.includes(seriesComment.author.id),
      },
    })
  )

  return (
    <Discussion
      type="series"
      isTutor={tutorIds.includes(currentUser.id)}
      comments={relevantDiscussionComments}
      onPost={handlePost}
      onDelete={handleDelete}
      hasMore={hasMore}
      onLoadMore={handleLoadMore}
    />
  )
}
